import React from 'react'
import { Helmet } from 'react-helmet-async'

// Interface for SEO component props
interface SEOProps {
  title: string
  description: string
  keywords?: string[]
  canonicalUrl?: string
  ogImage?: string
  structuredData?: any
}

// Default business address
const DEFAULT_ADDRESS = {
  "@type": "PostalAddress",
  streetAddress: "7321 Ridgeway Avenue",
  addressLocality: "Skokie",
  addressRegion: "IL",
  postalCode: "60076",
  addressCountry: "US"
}

// Default organization details
const DEFAULT_ORGANIZATION = {
  "@type": "LegalService",
  "name": "Adler & Herbach Real Estate Attorneys",
  "url": "https://calculators.northsidelegal.com",
  "address": DEFAULT_ADDRESS,
  "telephone": "+1 (847) 318-3770",
  "priceRange": "$750",
  "image": "https://calculators.northsidelegal.com/logo.png",
  "description": "Real estate attorneys specializing in home buying and legal services in Illinois"
}

// Centralized SEO component for consistent metadata
export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords = [],
  canonicalUrl = 'https://calculators.northsidelegal.com',
  ogImage = 'https://calculators.northsidelegal.com/og-image.png',
  structuredData
}) => {
  // Combine default and provided keywords
  const defaultKeywords = [
    'Illinois home buying',
    'mortgage calculators',
    'real estate tools'
  ]
  const combinedKeywords = [...defaultKeywords, ...keywords].join(', ')

  // Prepare structured data script
  const structuredDataScript = structuredData 
    ? createStructuredData({
        ...structuredData,
        address: structuredData.address || DEFAULT_ADDRESS,
        organization: structuredData.organization || DEFAULT_ORGANIZATION
      }) 
    : null

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={combinedKeywords} />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={ogImage} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={canonicalUrl} />
        <meta property="twitter:title" content={title} />
        <meta property="twitter:description" content={description} />
        <meta property="twitter:image" content={ogImage} />

        {/* Structured Data */}
        {structuredDataScript && (
          <script type="application/ld+json">
            {JSON.stringify(structuredDataScript)}
          </script>
        )}
      </Helmet>
    </>
  )
}

// Utility function to generate structured data
export const createStructuredData = (data: any) => {
  return {
    "@context": "https://schema.org",
    ...data
  }
}
